import { useEffect, useState } from 'react';

// ----------------------------------------------------------------------

// A section counts as active while it crosses a thin band just above the middle
// of the viewport, so only one section wins no matter how tall the sections are.
const ACTIVE_BAND_MARGIN = '-40% 0px -55% 0px';

/**
 * Returns the id of the section currently in view.
 * `sectionIds` must be in document order and referentially stable (module constant / memoized).
 */
export function useScrollSpy(sectionIds, { enabled = true } = {}) {
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setActiveId(null);
      return undefined;
    }

    const inBand = new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            inBand.add(entry.target.id);
          } else {
            inBand.delete(entry.target.id);
          }
        });

        // Resolve from the full set (not the last entry reported), so layout shifts
        // while the page is still loading can't leave a stale section active.
        const nextId = sectionIds.find((id) => inBand.has(id));

        if (nextId) {
          setActiveId(nextId);
        }
      },
      { rootMargin: ACTIVE_BAND_MARGIN, threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const section = document.getElementById(id);
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, [enabled, sectionIds]);

  return activeId;
}
